import { fetchCreatives, type CreativesParams } from './api'
import type { CreativesResponse } from './contract'
import { periodKey } from '../lib/period'

/**
 * What a list read is asked under, beside its wire parameters. The shell's `scope` carries
 * the client, the account selection and its revision; `reloadKey` moves on a guest's
 * re-read and on every committed assignment or merge. Both are part of what the screens
 * already trust a response to answer, so a body read under another pair is never theirs.
 */
export interface ReadTag {
  scope: string
  reloadKey: number
}

/**
 * One list read shared between callers that ask the same thing, and the last body each
 * asked thing was answered with.
 *
 * Two callers share a read only when every parameter is equal: the company, the window,
 * the comparison, the account set, the audience and the `as_of`, together with the tag.
 * The sidebar badge asks for every audience without a comparison and the creatives screen
 * for the selected audience with one, so those two never share; the badge and the Ideas
 * base request do. A badge that reused a one-audience body would count wrong, and a screen
 * that reused the badge's body would fail its own echo check, which is why equality is the
 * whole rule and no body is ever offered under another key.
 *
 * The retained body is what a screen paints first when it mounts again under a key it has
 * already read: a switch between Creatives and Ideas and back. It is not a reason to skip
 * the read; each screen still re-reads on mount, as it did before, and replaces the body.
 */
export interface ListReads {
  /** The shared read for these parameters. Rejects with an `AbortError` on `signal`. */
  read(params: CreativesParams, tag: ReadTag, fetchImpl: typeof fetch, signal: AbortSignal): Promise<CreativesResponse>
  /** The last body read under exactly these parameters and this tag, or `null`. */
  retained(params: CreativesParams, tag: ReadTag): CreativesResponse | null
  /**
   * Starts the read for these parameters now, held for the next `read` of the same key.
   *
   * A period click commits a render of the whole screen before the screen's effect asks
   * for the new window, so the request waited for that render (GRO-180). The shell calls
   * this from the click instead. The hold counts as one subscriber until the first `read`
   * of the key joins, or until `PREFETCH_HOLD_MS` pass, after which a flight nobody joined
   * is aborted like any abandoned read. An answer or an error that lands while the hold
   * exists stays joinable under the key until the hold ends.
   */
  prefetch(params: CreativesParams, tag: ReadTag, fetchImpl: typeof fetch): void
}

/** The bodies kept: enough for the two screens, the badge and a couple of windows. */
export const RETAINED_LIMIT = 8

/** How long a prefetched read waits for its reader. Far above the slowest click render
 * measured (160 ms), so the hold ends only for a key no screen asked for. */
export const PREFETCH_HOLD_MS = 2000

export function readKey(params: CreativesParams, tag: ReadTag): string {
  const accounts = params.accountIds === null ? '*' : [...params.accountIds].sort().join(',')
  return [
    tag.scope,
    `k${tag.reloadKey}`,
    params.companyId,
    periodKey(params.period),
    params.compare ? 'compare' : '-',
    accounts,
    params.audienceId ?? '*',
    params.asOf ?? '-',
  ].join('|')
}

interface Flight {
  key: string
  controller: AbortController
  promise: Promise<CreativesResponse>
  subscribers: number
  /** Every subscriber left before the answer: the answer is nobody's and the key is free. */
  abandoned: boolean
  /** A prefetch's hold, still waiting for the first reader; counted in `subscribers`. */
  hold: ReturnType<typeof setTimeout> | null
  settled: boolean
}

function abandoned(): DOMException {
  return new DOMException('the read was abandoned', 'AbortError')
}

export function createListReads(limit = RETAINED_LIMIT): ListReads {
  const flights = new Map<string, Flight>()
  const bodies = new Map<string, CreativesResponse>()

  const keep = (key: string, body: CreativesResponse) => {
    bodies.delete(key)
    bodies.set(key, body)
    while (bodies.size > limit) {
      const oldest = bodies.keys().next().value
      if (oldest === undefined) break
      bodies.delete(oldest)
    }
  }

  const start = (key: string, params: CreativesParams, fetchImpl: typeof fetch): Flight => {
    const controller = new AbortController()
    const flight: Flight = {
      key,
      controller,
      subscribers: 0,
      abandoned: false,
      hold: null,
      settled: false,
      // A held flight keeps its key after it settles, so the reader the hold waits for
      // joins the answer instead of starting the same request again.
      promise: fetchCreatives(params, fetchImpl, controller.signal).then(
        (body) => {
          flight.settled = true
          if (flight.hold === null && flights.get(key) === flight) flights.delete(key)
          if (!flight.abandoned) keep(key, body)
          return body
        },
        (error: unknown) => {
          flight.settled = true
          if (flight.hold === null && flights.get(key) === flight) flights.delete(key)
          throw error
        },
      ),
    }
    flights.set(key, flight)
    return flight
  }

  // The hold leaves as a subscriber does: the last one out aborts a request still in
  // flight. A settled flight gives up its key either way, because it was kept only for
  // the reader the hold was waiting on.
  const release = (flight: Flight) => {
    if (flight.hold === null) return
    clearTimeout(flight.hold)
    flight.hold = null
    flight.subscribers -= 1
    if (flight.settled) {
      if (flights.get(flight.key) === flight) flights.delete(flight.key)
    } else if (flight.subscribers === 0) {
      flight.abandoned = true
      if (flights.get(flight.key) === flight) flights.delete(flight.key)
      flight.controller.abort()
    }
  }

  // One subscriber's abort ends its own promise and nothing else; the request itself is
  // aborted only when the last subscriber has left it. An abandoned request also gives up
  // its key at once: a fetch that ignores its signal, or is slow to honour it, must not be
  // what the next reader of the same key waits on.
  const join = (flight: Flight, signal: AbortSignal): Promise<CreativesResponse> =>
    new Promise((resolve, reject) => {
      flight.subscribers += 1
      let settled = false
      const leave = () => {
        if (settled) return
        settled = true
        signal.removeEventListener('abort', onAbort)
        flight.subscribers -= 1
        if (flight.subscribers === 0) {
          flight.abandoned = true
          if (flights.get(flight.key) === flight) flights.delete(flight.key)
          flight.controller.abort()
        }
        reject(abandoned())
      }
      const onAbort = () => leave()
      if (signal.aborted) {
        leave()
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
      flight.promise.then(
        (body) => {
          if (settled) return
          settled = true
          signal.removeEventListener('abort', onAbort)
          resolve(body)
        },
        (error: unknown) => {
          if (settled) return
          settled = true
          signal.removeEventListener('abort', onAbort)
          reject(error)
        },
      )
    })

  return {
    read(params, tag, fetchImpl, signal) {
      const key = readKey(params, tag)
      const flight = flights.get(key) ?? start(key, params, fetchImpl)
      const joined = join(flight, signal)
      release(flight)
      return joined
    },
    retained(params, tag) {
      return bodies.get(readKey(params, tag)) ?? null
    },
    prefetch(params, tag, fetchImpl) {
      const key = readKey(params, tag)
      if (flights.has(key)) return
      const flight = start(key, params, fetchImpl)
      // Every other flight is joined at once, and the join is what handles its rejection.
      // Nobody may join this one, so it observes its own; readers still get the error.
      flight.promise.catch(() => {})
      flight.subscribers = 1
      flight.hold = setTimeout(() => release(flight), PREFETCH_HOLD_MS)
    },
  }
}
